import { ToastMessage } from '@cokitchen/cokitchen-components';
import useDashboardContext from 'contexts/dashboard.context';
import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import FeedCard from 'components/feed-card';
import { ReactComponent as EditIcon } from 'assets/icons/edit-meal-icon.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive-icon.svg';
import DeleteModal from 'components/base/delete-modal';
import { MealType } from 'utils/types';
import { apiService } from 'services/base.service';
import Tabs from 'components/base/tabs';
import { toast } from 'react-toastify';
import SearchNav from 'components/search-nav';
import { numberWithCommas } from 'utils/functions';
import SoldoutModal from 'components/sold-out-modal';
import moment from 'moment';

const ManageBaseMeal = () => {
  const { setIsCustomSearch, customSearch, activeCokitchen } = useDashboardContext();
  const [baseMeals, setBaseMeals] = useState([] as MealType[]);
  const [active, setActive] = useState({} as MealType);
  const [loading, setLoading] = useState(false);
  const [soldoutLoading, setSoldoutLoading] = useState(false);

  const [deleteLoading, setDeleteLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSoldoutModal, setShowSoldoutModal] = useState(false);

  const [selectedBaseMeals, setSelectedBaseMeals] = useState<{ id: string; name: string }[]>([]);
  const [activeFilter, setActiveFilter] = useState<{
    id: string;
    name: string;
    subtext?: number;
  }>({
    name: 'All',
    id: 'all'
  });
  const [isSoldOut, setIsSoldOut] = useState<string[]>(['']);
  const mealStatus: Record<string, string> = {
    all: 'all',
    available: 'available',
    unavailable: 'unavailable',
    soldOut: 'soldout'
  };
  useEffect(() => {
    setIsCustomSearch(true);
    return () => {
      setIsCustomSearch(false);
    };
  }, []);

  const fetchMoreData = () => {
    setPage((p) => p + 1);
  };
  const soldOutMeal = async (data: any) => {
    setSoldoutLoading(true);
    const ids =
      selectedBaseMeals?.length > 1 ? selectedBaseMeals.map((item) => item.id) : [active?.id];
    const {
      quantity,
      isInfinity,
      soldOutMinutes,
      active: isActive,
      targetRelatedMeals,
      targetRelatedAddons
    } = data;

    const payload = {
      base_meal_ids: ids,
      active: !isActive,
      available_no: isInfinity ? 'INFINITE' : quantity.toString(),
      availability_soon_date: soldOutMinutes === 0 ? null : moment().add(soldOutMinutes, 'minutes'),
      cokitchen_id: activeCokitchen?.id,
      target_related_addons: targetRelatedAddons,
      target_related_meals: targetRelatedMeals
    };

    const res = await apiService.patch(`base/meals/bulk`, { data: payload });
    if (res) {
      setSelectedBaseMeals([]);

      toast(<ToastMessage type='success' message='Meal has been updated' />);
      getBaseMeals();
    }
    setSoldoutLoading(false);
    setShowSoldoutModal(false);
  };

  const handleChange = (base: MealType) => {
    if (selectedBaseMeals?.includes(base)) {
      setSelectedBaseMeals(selectedBaseMeals?.filter((item) => item.id !== base.id));
    } else {
      setSelectedBaseMeals([...selectedBaseMeals, base]);
    }
  };

  const getBaseMeals = async () => {
    try {
      if (page === 1) setLoading(true);
      const params: { page: number; search?: string; active?: boolean; meal_status?: any } = {
        page,
        meal_status: mealStatus[activeFilter.id]
      };
      if (customSearch !== '') {
        params.search = customSearch;
      }

      const res: any = await apiService.get('base/meals', {
        params
      });
      const response = res?.data?.data?.data;

      if (response) {
        const soldOut = response
          .filter((item: MealType) => item.active === false)
          .map((item: MealType) => item.id);

        if (page === 1) {
          setBaseMeals(response);
          setIsSoldOut([...soldOut]);
        } else {
          setBaseMeals((prev) => [...prev, ...response]);
          setIsSoldOut([...isSoldOut, ...soldOut]);
        }
        setTotal(res.data.data.pagination.total);
      }
    } catch (error: any) {
      toast(<ToastMessage type='error' message={error.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const getMeals = setTimeout(() => {
      setPage(1);
      getBaseMeals();
    }, 1000);

    return () => {
      clearTimeout(getMeals);
    };
  }, [customSearch]);

  useEffect(() => {
    getBaseMeals();
  }, [page, activeFilter]);

  useEffect(() => {
    setPage(1);
  }, [activeFilter]);

  const handleSelectAll = () => {
    setSelectedBaseMeals(baseMeals);
  };
  const updateBaseMeal = async (data: any, onSuccess: () => void) => {
    setDeleteLoading(true);
    try {
      const res: any = await apiService.patch('base/meals/bulk', {
        data
      });
      if (res.data) {
        toast(<ToastMessage type='success' message={'Updated base meal successfully'} />);
        setSelectedBaseMeals([]);
        setShowDeleteModal(false);
        onSuccess();
        setBaseMeals(
          baseMeals.filter(
            (item) =>
              !selectedBaseMeals
                .map((item) => {
                  return item.id;
                })
                .includes(item.id)
          )
        );
      }
    } catch (error: any) {
      toast(<ToastMessage type='error' message={error.response.data.message} />);
    } finally {
      setDeleteLoading(false);
    }
  };
  const handleDelete = () => {
    updateBaseMeal(
      {
        base_meal_ids: selectedBaseMeals.map((item) => {
          return item.id;
        }),
        archived: true
      },
      () => getBaseMeals()
    );
  };

  return (
    <div>
      <Tabs
        list={[
          { name: 'All', id: 'all' },
          { name: 'Available', id: 'available' },
          { name: 'Unavailable', id: 'unavailable' },

          { name: 'Sold out', id: 'soldOut' }
        ]}
        setActiveFilter={setActiveFilter}
      />

      <div>
        {loading ? (
          <div className='w-full mt-10 flex justify-center items-center'>
            <p className='font-medium text-lg'>Loading...</p>
          </div>
        ) : baseMeals?.length === 0 ? (
          <div className='w-full mt-10 flex justify-center items-center'>
            <p className='font-medium text-lg'>No base meal available</p>
          </div>
        ) : (
          <InfiniteScroll
            className='no-scrollbar'
            dataLength={baseMeals.length}
            next={() => fetchMoreData()}
            hasMore={total > baseMeals.length}
            loader={<h4 className='text-center'>Loading...</h4>}
            height='75vh'
            endMessage={
              <p className='text-center'>
                <b>Yay! You have seen it all</b>
              </p>
            }>
            <div className='grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5'>
              {baseMeals?.map((baseMeal) => (
                <FeedCard
                  key={baseMeal.id}
                  showCheckBox
                  checked={selectedBaseMeals.includes(baseMeal)}
                  onSelect={() => handleChange(baseMeal)}
                  image={baseMeal.images?.[0]}
                  className='mb-2'
                  actions={
                    selectedBaseMeals.length === 0 && (
                      <div className='flex gap-2 absolute top-2 right-2'>
                        <ArchiveIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDeleteModal(true);
                            setActive(baseMeal);
                          }}
                          className={` w-7 h-7 text-white bg-black hover:bg-black bg-opacity-70 rounded-md p-[7.5px] transition-none z-10`}
                        />
                        <EditIcon
                          onClick={() => {
                            setActive(baseMeal);
                            setShowSoldoutModal(true);
                          }}
                          className=' w-7 h-7 text-white bg-black hover:bg-black bg-opacity-70 rounded-md p-[7.5px] transition-none z-10 edit_icon'
                        />
                      </div>
                    )
                  }>
                  <div className='flex justify-between items-center mt-2'>
                    <div className='flex gap-1 items-center'>
                      <div
                        className='h-[40px] w-[40px] rounded-[100%]'
                        style={{
                          backgroundImage: `url(${baseMeal?.brand?.logo && baseMeal?.brand?.logo})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />
                      <div>
                        <p className='text-sm'>{baseMeal?.name}</p>
                        {baseMeal?.amount && (
                          <p className='text-xs text-gray-5'>
                            &#8358;{numberWithCommas(baseMeal?.amount)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>{' '}
                </FeedCard>
              ))}
            </div>
          </InfiniteScroll>
        )}
      </div>
      {selectedBaseMeals?.length > 0 && (
        <SearchNav
          count={selectedBaseMeals.length}
          handleSelectAll={handleSelectAll}
          onDoneClick={() => setSelectedBaseMeals([])}
          onEditClick={() => {
            setActive({} as MealType);
            setShowSoldoutModal(true);
          }}
          onDeleteClick={() => {
            setActive({} as MealType);
            setShowDeleteModal(true);
          }}
        />
      )}
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
        title={`Are you sure you want to delete ${
          selectedBaseMeals.length > 0 ? 'base meals' : active?.name
        }`}
        description='This action cannot be undone'
        loading={deleteLoading}
      />
      <SoldoutModal
        meal={active}
        show={showSoldoutModal}
        onClose={() => setShowSoldoutModal(false)}
        onConfirm={soldOutMeal}
        loading={soldoutLoading}
      />
    </div>
  );
};

export default ManageBaseMeal;
